import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { isEqual, orderBy } from 'lodash';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DataDefinition, DataDefinitionList } from '../../../admin360/data-definition/data-definition.model';
import { CustomizationFrameworkService } from '../../../shared-modules/customization-framework/customization-framework.service';
import { LoginService } from '../../../shared-modules/login/login.service';
import { YearService } from '../../year/year.service';

@AutoUnsubscribe({ includeArrays: true })
@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class DataDefControl implements OnInit, OnDestroy {

  // NOTE: This class is inherited by the various types of data def controls (dropdown, radio button list, multi-select, etc)
  // Some methods in this class are overridden by it's children

  static incrementor = 1;
  localId: number;
  subscriptions: Subscription[] = [];
  dataDef: DataDefinitionList;
  options: DataDefinition[] = [];
  showButton = false;
  rawValue: any;
  private _dataDefEnum: string;
  public get dataDefEnum(): string {
    return this._dataDefEnum;
  }
  public _value: any;
  public get value(): any {
    return this._value;
  }
  private _useCodeAsValue: boolean;
  public get useCodeAsValue(): boolean {
  return this._useCodeAsValue;
  }
  private _useDefaultValue: boolean;
  public get useDefaultValue(): boolean {
  return this._useDefaultValue;
  }

  @Input() label: string;
  @Input() required: boolean;
  @Input() hideRequiredMarker: boolean;
  @Input() disabled: boolean;
  @Input()public set useDefaultValue(value: boolean) {
    this._useDefaultValue = value;
    this.setDefaultValue();
  }
  @Input() public set useCodeAsValue(value: boolean) { // if true we will use the Code as the value rather than the ID
    this._useCodeAsValue = value;
    this.setValue(this.rawValue);
  }
  @Input() public set dataDefEnum(value: string) {
    this._dataDefEnum = value;
    this.getDataDef();
  }
  @Input() public set value(value: any) {
    this.rawValue = value;
    if (this._value != value && this._value != Number(value) || (Array.isArray(value) && !isEqual(value, this.value))) {
       this.setValue(value);
    }
  }

  @Output() valueChange = new EventEmitter<any>();
  @Output() controlEmitter = new EventEmitter<ElementRef>();
  @Output() codeChange = new EventEmitter<string>();
  @Output() descriptionChange = new EventEmitter<string>();

  constructor(
    private customizationFrameworkService: CustomizationFrameworkService,
    private yearService: YearService,
    private loginService: LoginService
  ) {
    this.localId = DataDefControl.incrementor++;
  }

  ngOnInit(): void {
    // this.getDataDef();  // this will be called when the lookup enum input is populated

    this.subscriptions.push(this.yearService.observableDefaultYearStream$.subscribe(y => {
      this.getDataDef();
    }));
  }

  ngOnDestroy() { }

  getDataDef() {
    this.customizationFrameworkService.getDataDefinitionListByEnum(this.dataDefEnum, this.yearService.defaultYear.Id, this.yearService.defaultYear.SiteYearId, null).pipe(take(1)).subscribe(list => {
      this.dataDef = list;
      this.checkPermissions();
      this.filterOptions();
      this.setDefaultValue();
    });
  }

  setValue(value: any) {
    if (!value || this.useCodeAsValue || Array.isArray(value)) {
      this._value = value;
    }
    else {
      this._value = Number(value);
    }
    this.filterOptions();
    this.setDefaultValue();
  }

  // this method is overridden by the multi-select control
  setDefaultValue() {
    if (this.options && !this.value && this.useDefaultValue) {
      const defaultOption = this.options.find(o => o.IsDefault && !o.Inactive);
      if (defaultOption) {
        if (this.useCodeAsValue) {
          this._value = defaultOption.Code;
        }
        else {
          this._value = defaultOption.Id;
        }
        this.valueChangeHandler();
      }
    }
  }

  // this method is overridden by the multi-select control
  filterOptions() {
    if (this.dataDef) {
      this.options = this.dataDef.DataDefinitions.filter(o =>
        (
          o.Inactive == false  // do not show inactive codes unless that is the currently selected value
          ||
          (
            this.value && o.Id
            && (
              (!this.useCodeAsValue && o.Id == this.value)
              || (this.useCodeAsValue && o.Code == this.value)
            )
          )
        )
        &&
        (
          (  // We only show list items that have a valid code ('blank' is not valid) or a valid description
            o.Code && o.Code.trim() && o.Code.trim().toLowerCase() != 'blank'
          )
          ||
          (
            o.Description && o.Description.trim()
          )
        )
      );
      this.sortOptions();
      this.processOptions();
    }
  }

  sortOptions() {
    this.options = orderBy(this.options, ['Sequence'], ['asc']);
  }

  checkPermissions() {
    if (this.loginService.LoginSettings.EnableDataDefinitions && this.dataDef && !this.dataDef.ReadOnly && this.dataDef.UserCanEdit) {
      this.showButton = true;
    }
  }

  // this method is overridden by the multi-select control
  valueChangeHandler() {
    this.valueChange.emit(this.value);
    let optionSelected: DataDefinition;
    if (this.useCodeAsValue) {
      optionSelected = this.options.find(option => option.Code == this.value);
    }
    else {
      optionSelected = this.options.find(option => option.Id == this.value);
    }

    if (optionSelected) {
      this.descriptionChange.emit(optionSelected.Description);
      this.codeChange.emit(optionSelected.Code);
    }
  }

  public processOptions(): void { }  // should be overridden by children if extra processing is necessary, such as in radio button list and multi-select
}
